import moment from 'moment-timezone';
import PaperModal from 'raydiant-elements/core/PaperModal';
import TimeField from 'raydiant-elements/core/TimeField';
import Link from 'raydiant-elements/core/Link';
import InputHelperText from 'raydiant-elements/core/InputHelperText';
import Text from '@raydiant/raydial/components/Text';
import Button from '@raydiant/raydial/components/Button';
import Heading from 'raydiant-elements/core/Heading';
import Toggle from '@raydiant/raydial/components/Toggle';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import Column from 'raydiant-elements/layout/Column';
import Scrollable from 'raydiant-elements/layout/Scrollable';
import { useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as D from '../../../clients/mira/types/Device';
import * as devicePageActions from '../actions';
import {
  selectIsEnterpriseUser,
  selectUserProfile,
  selectIsVIOnly,
  selectIsLiteUser,
} from '../../../selectors/user';
import { canShareResource } from '../../../utilities';
import Row from 'raydiant-elements/layout/Row/Row';
import useStyles from './DeviceBatchSettings.styles';
import DeviceBatchTags from './DeviceBatchTags';
import DeviceBatchDialog from './DeviceBatchDialog';
import { selectBatchDeviceSettings } from '../selectors';
import { BatchDevice } from '../deviceBatchSettings.interface';
import DeviceBatchSharing from './DeviceBatchSharing';
import { initialBatchDeviceSettings } from '../reducer';
import isEqual from 'lodash/isEqual';
import { formatToBatchData } from '../deviceBatchSettingsUtils';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { LoadingStatus } from '../../../components/LoadingButton/LoadingButton';
import apiClient from '../../../clients/miraClient';
import { useTranslation } from 'react-i18next';
import TimezoneSelect from '../components/TimezoneSelect';
import config from '../../../config';

const { supportAppUrl } = config;

const fromTimeFieldValue = (time: string | null) => {
  if (!time) return '';

  return moment(time, 'HH:mm').format('HH:mm:ss');
};

const toTimeFieldValue = (time: string | null) => {
  if (!time) return '';

  return moment(time, 'HH:mm:ss').format('HH:mm');
};

const maximumDeviceUpdateBlackoutRange = 21;

interface DeviceSettingsProps {
  devices: D.Device[];
  onPlaylistSelectorClick: () => void;
  lastLoadedDate: string;
  onDialogClick: () => void;
  onClose: () => void;
}

const DeviceBatchSettings = ({
  devices,
  lastLoadedDate,
  onPlaylistSelectorClick,
  onDialogClick,
  onClose,
}: DeviceSettingsProps) => {
  const { t } = useTranslation(['devices']);
  const dispatch = useDispatch();
  const classes = useStyles();
  const [device] = useState(devices[0]); //initialize device props to address race condition when creating new tags
  // Selectors

  const currentUser = useSelector(selectUserProfile);
  const isEnterpriseUser = useSelector(selectIsEnterpriseUser);
  const deviceBatchSettings = useSelector(selectBatchDeviceSettings);
  const isVIOnly = useSelector(selectIsVIOnly);
  const isLiteUser = useSelector(selectIsLiteUser);

  // State
  // Device Batch Settings
  const [open, setOpen] = useState<boolean>(false);
  const [batchSettingsLoadingStatus, setBatchSettingsLoadingStatus] =
    useState<LoadingStatus>('idle');
  const [updatedMaintenanceWindowEnabled, setMaintenanceWindowEnabled] =
    useState<boolean | null>(null);
  const [maintenanceWindowValid, setMaintenanceWindowValid] =
    useState<boolean>(false);

  // Callbacks

  const updateDevice = useCallback(
    (settings: Partial<BatchDevice>) => {
      dispatch(devicePageActions.updateBatchDeviceSettings(settings));
    },
    [dispatch],
  );

  const validate = useCallback(() => {
    let isValid = true;
    setMaintenanceWindowValid(isValid);

    const { activeHoursStart, activeHoursEnd } = deviceBatchSettings;

    // skip validation check if either timefield value is empty
    if (!activeHoursStart || !activeHoursEnd) {
      return;
    }

    const startTime = moment(activeHoursStart, 'HH:mm:ss');
    const endTime = moment(activeHoursEnd, 'HH:mm:ss');

    let duration = moment.duration(endTime.diff(startTime));

    // if start date is greater than end date, add a day to the end date and re-calculate difference
    if (duration.hours() < 0) {
      endTime.add(1, 'day');
      duration = moment.duration(endTime.diff(startTime));
    }

    if (
      duration.hours() > maximumDeviceUpdateBlackoutRange ||
      (duration.hours() === maximumDeviceUpdateBlackoutRange &&
        duration.minutes() > 0)
    ) {
      isValid = false;
    }

    setMaintenanceWindowValid(isValid);
    return isValid;
  }, [deviceBatchSettings]);

  const updateActiveHoursTime = useCallback(() => {
    if (!validate()) return;

    updateDevice({
      activeHoursStart: fromTimeFieldValue(
        deviceBatchSettings.activeHoursStart,
      ),
      activeHoursEnd: fromTimeFieldValue(deviceBatchSettings.activeHoursEnd),
    });
  }, [
    updateDevice,
    validate,
    deviceBatchSettings.activeHoursStart,
    deviceBatchSettings.activeHoursEnd,
  ]);

  const closeBatchSettingsConfirmation = useCallback(() => {
    setOpen(false);
  }, []);

  const handleAutoPublish = useCallback(
    (autoPublish: boolean) => {
      if (deviceBatchSettings.autoPublish === autoPublish) return;

      updateDevice({ autoPublish });
    },
    [deviceBatchSettings.autoPublish, updateDevice],
  );

  const handleDialogClick = useCallback(async () => {
    setBatchSettingsLoadingStatus('loading');

    try {
      const selectedDeviceIds = Object.values(devices).map(
        (device) => device.id,
      );
      const params = formatToBatchData(selectedDeviceIds, deviceBatchSettings);
      await apiClient.batchDeviceSettings(params);
      setBatchSettingsLoadingStatus('success');
      onDialogClick();
      setOpen(false);
    } catch (error) {
      console.log('ERROR APPLYING BATCH SETTINGS', error);
      setBatchSettingsLoadingStatus('error');
    }
  }, [deviceBatchSettings, devices, onDialogClick]);

  // Side-effects

  // Update maintenance window toggle status when changed.
  useEffect(() => {
    if (updatedMaintenanceWindowEnabled === null) return;

    if (!updatedMaintenanceWindowEnabled) {
      updateDevice({
        activeHoursStart: null,
        activeHoursEnd: null,
      });
    }
  }, [updateDevice, updatedMaintenanceWindowEnabled]);

  // Render

  const isShareable =
    isEnterpriseUser &&
    currentUser &&
    canShareResource(currentUser, device.resource);

  const maintenanceWindowSet = !!(
    deviceBatchSettings.activeHoursStart && deviceBatchSettings.activeHoursEnd
  );

  const maintenanceWindowEnabled =
    updatedMaintenanceWindowEnabled ?? maintenanceWindowSet ?? false;

  const validActiveHours = updatedMaintenanceWindowEnabled
    ? maintenanceWindowSet && maintenanceWindowValid
    : maintenanceWindowValid;

  const disableApplyButton =
    (updatedMaintenanceWindowEnabled && !validActiveHours) ||
    isEqual(deviceBatchSettings, initialBatchDeviceSettings);

  return (
    <>
      <DeviceBatchDialog
        open={open}
        status={batchSettingsLoadingStatus}
        onClose={closeBatchSettingsConfirmation}
        onHandleAutoPublish={handleAutoPublish}
        onApplyBatchSettings={handleDialogClick}
        settings={deviceBatchSettings}
      />
      <PaperModal.Body>
        <Column>
          <div>
            <Text variant="subheader" className="text-black mb-1 flex">
              {device.name}
            </Text>
            <Heading size={5} className={classes.type}>
              {devices.length} devices selected
            </Heading>
          </div>
        </Column>
        <br />
      </PaperModal.Body>
      <Scrollable>
        <PaperModal.Body>
          <div className={classes.section}>
            <Column doubleMargin>
              {!isVIOnly && (
                <div>
                  <Button fullWidth onPress={onPlaylistSelectorClick}>
                    Assign playlist
                  </Button>
                  {deviceBatchSettings.playlist && (
                    <Row
                      key="playlist"
                      halfMargin
                      className={classes.playlistRow}
                    >
                      <div className={classes.playlistName}>
                        Selected Playlist: {deviceBatchSettings.playlist.name}
                      </div>
                      <button
                        className={classes.remove}
                        onClick={() => {
                          updateDevice({ playlistId: null, playlist: null });
                        }}
                      >
                        <HighlightOffIcon />
                      </button>
                    </Row>
                  )}

                  <InputHelperText>
                    Will affect {devices.length} screens
                  </InputHelperText>
                </div>
              )}

              {!isVIOnly && (
                <Select
                  label="Screen Resolution"
                  selectedKey={deviceBatchSettings.screenDimensions}
                  onSelectionChange={(value) =>
                    updateDevice({
                      screenDimensions: value as string,
                    })
                  }
                >
                  <SelectItem key="" id="">
                    Select to apply
                  </SelectItem>
                  <SelectItem key="1920x1080" id="1920x1080">
                    1080p
                  </SelectItem>
                  <SelectItem key="1280x720" id="1280x720">
                    720p
                  </SelectItem>
                </Select>
              )}

              <TimezoneSelect
                label="Timezone"
                onChange={(value) =>
                  updateDevice({
                    timezone: value,
                  })
                }
                value={deviceBatchSettings.timezone}
                batch
              />

              {!isVIOnly && (
                <Select
                  label="Screen Orientation"
                  selectedKey={deviceBatchSettings.screenOrientation}
                  onSelectionChange={(value) =>
                    updateDevice({
                      screenOrientation: value as string,
                    })
                  }
                >
                  <SelectItem id="" key="">
                    Select to apply
                  </SelectItem>
                  <SelectItem id="normal" key="normal">
                    {t(`screens.landscape`)}
                  </SelectItem>
                  <SelectItem id="right" key="right">
                    {t(`screens.portraitRight`)}
                  </SelectItem>
                  <SelectItem id="left" key="left">
                    {t(`screens.portraitLeft`)}
                  </SelectItem>
                  <SelectItem id="inverted" key="inverted">
                    {t(`screens.inverted`)}
                  </SelectItem>
                </Select>
              )}

              {!isLiteUser && (
                <div className={classes.section}>
                  <DeviceBatchTags devices={devices} />
                </div>
              )}

              {isShareable && (
                <div className={classes.section}>
                  <DeviceBatchSharing devices={devices} />
                </div>
              )}
            </Column>
          </div>

          {!isLiteUser && (
            <div className={classes.section}>
              <Column>
                <Text variant="smallSubheader" className="text-black mb-3 flex">
                  Device Management
                </Text>
                <Text as="label" htmlFor="deviceBlackoutWindow">
                  Device Update Blackout
                </Text>
                <Toggle
                  id="deviceBlackoutWindow"
                  isSelected={maintenanceWindowEnabled}
                  onChange={setMaintenanceWindowEnabled}
                >
                  Specifies a time of day in which no software updates should
                  occur.
                </Toggle>

                {maintenanceWindowEnabled && (
                  <Row halfMargin>
                    <TimeField
                      helperText={
                        <span>
                          Maximum range of {maximumDeviceUpdateBlackoutRange}{' '}
                          hours.{' '}
                          <Link
                            href={`${supportAppUrl}/getting-started/5imknKqrcfswGZ9RmtHfPK/dashboard-introduction-and-basics/5imknKqrcinJNiXdEoT8cC`}
                            target="_blank"
                          >
                            Learn more
                          </Link>
                        </span>
                      }
                      label="from"
                      value={
                        toTimeFieldValue(
                          deviceBatchSettings.activeHoursStart,
                        ) || ''
                      }
                      onChange={(value) =>
                        updateDevice({
                          activeHoursStart: fromTimeFieldValue(value) || null,
                        })
                      }
                      error={!maintenanceWindowValid}
                      onBlur={updateActiveHoursTime}
                    />
                    <TimeField
                      label="to"
                      value={
                        toTimeFieldValue(deviceBatchSettings.activeHoursEnd) ||
                        ''
                      }
                      onChange={(value) =>
                        updateDevice({
                          activeHoursEnd: fromTimeFieldValue(value) || null,
                        })
                      }
                      error={!maintenanceWindowValid}
                      onBlur={updateActiveHoursTime}
                    />
                  </Row>
                )}
              </Column>
            </div>
          )}
          <Button
            fullWidth
            isDisabled={disableApplyButton}
            onPress={() => setOpen(true)}
          >
            Apply
          </Button>
        </PaperModal.Body>
      </Scrollable>
    </>
  );
};

export default DeviceBatchSettings;
